/**
 * GoobDoob VTT — scene data model.
 *
 * TypeScript mirror of src/lambda/shared/GoobDoobVtt.Domain/Scene.cs.
 * Both implementations must stay in lockstep — when one changes, the other has
 * to change too. The reconciler in particular has to produce identical results
 * on client and server or convergence under concurrent edits breaks.
 *
 * Every drawable thing on a map is a {@link SceneElement} identified by id.
 * Elements are persisted per-map under PK = `MAP#<mapId>`, SK = `ELEMENT#<id>`.
 *
 * Collaboration is last-write-wins per element using
 * `(version, versionNonce)`. See {@link reconcile} for the tie-breaking rules.
 */

export type ElementType =
  | 'freedraw'
  | 'rect'
  | 'ellipse'
  | 'line'
  | 'arrow'
  | 'text'
  | 'image';

/**
 * Visibility + edit rules.
 *   `dm`     — only the DM ever sees it. Stripped server-side from non-DM subscribes.
 *   `shared` — everyone sees it. The default layer.
 *   `player` — private scratch layer owned by one player. Visible to that
 *              player and the DM only. Persists across sessions.
 */
export type SceneLayer = 'dm' | 'shared' | 'player';

export type StrokeStyle = 'solid' | 'dashed' | 'dotted';
export type FillStyle = 'solid' | 'hachure' | 'none';

/**
 * Flat element representation. All per-type fields are optional and only
 * meaningful for the matching `type`; keeping the class flat lets DynamoDB
 * round-trip a row in a single PutItem/GetItem and halves the on-wire JSON
 * size versus a discriminated union with nested objects.
 */
export interface SceneElement {
  id: string;
  mapId: string;
  type: ElementType;
  ownerId: string;
  layer: SceneLayer;

  // geometry
  x: number;
  y: number;
  width: number;
  height: number;
  angle: number;

  // style
  strokeColor: string;
  backgroundColor?: string | null;
  strokeWidth: number;
  strokeStyle: StrokeStyle;
  fillStyle: FillStyle;
  roughness: number;
  opacity: number;

  // freedraw / linear
  /**
   * Flat point list relative to (x, y): [x0, y0, x1, y1, ...]. For freedraw
   * the first point is always (0, 0). Only set for freedraw, line, arrow.
   */
  points?: number[];
  /** Pen pressure samples, one per point. Only set for freedraw. */
  pressures?: number[];

  // text
  text?: string;
  fontSize?: number;

  // image
  fileId?: string;
  naturalWidth?: number;
  naturalHeight?: number;

  // versioning
  /** Monotonic per element. Bumps by 1 on every mutation. */
  version: number;
  /** Random tiebreaker when two clients write the same version concurrently. */
  versionNonce: number;
  /** Epoch ms. Informational — version is authoritative. */
  updatedAt: number;
  /** Soft delete — retained for reconciliation but not rendered. */
  isDeleted: boolean;
}

/**
 * Op envelope matching C# SceneOp. All `scene/op` messages have this shape,
 * carrying the full post-mutation element snapshot. Sending snapshots instead
 * of patches keeps reconciliation trivially correct at the cost of bandwidth —
 * worth it for the <10-player sessions this app targets.
 */
export interface SceneOp {
  mapId: string;
  element: SceneElement;
  /**
   * Originating connection id — filled in by the server on rebroadcast so
   * clients can suppress their own echoes. Clients leave it undefined on send.
   */
  origin?: string;
}

// ---------------------------------------------------------------------------
// Reconciliation
//
// Rules, in order:
//   1. Higher version wins.
//   2. On equal version, higher versionNonce wins.
//   3. On equal version AND equal nonce, the existing element is kept (stable,
//      idempotent under replay).
//
// Identical logic must exist in SceneReconciler.cs. Tests for both live in
// GoobDoobVtt.Domain.Tests/SceneReconcilerTests.cs (C#). If you change the
// rules here, add matching TS tests and update the C# file in the same commit.
// ---------------------------------------------------------------------------

/**
 * Compare two versions of the same element id.
 *
 * @returns `>0` if `incoming` should replace `existing`,
 *          `<0` if `existing` should win,
 *          `0` if they are identical by version and nonce.
 */
export function compare(existing: SceneElement, incoming: SceneElement): number {
  if (existing.id !== incoming.id) {
    throw new Error(
      `Cannot reconcile different elements (${existing.id} vs ${incoming.id})`,
    );
  }
  if (incoming.version !== existing.version) {
    return incoming.version > existing.version ? 1 : -1;
  }
  if (incoming.versionNonce !== existing.versionNonce) {
    return incoming.versionNonce > existing.versionNonce ? 1 : -1;
  }
  return 0;
}

/**
 * Returns the winning element. On a tie (same version and nonce) the existing
 * element is returned unchanged.
 */
export function reconcile(
  existing: SceneElement,
  incoming: SceneElement,
): SceneElement {
  return compare(existing, incoming) > 0 ? incoming : existing;
}

/**
 * True when `incoming` strictly supersedes `existing`. The client uses this to
 * decide whether a server echo actually displaced local state and therefore
 * whether to rewind and replay pending ops.
 */
export function shouldApply(
  existing: SceneElement,
  incoming: SceneElement,
): boolean {
  return compare(existing, incoming) > 0;
}

/**
 * Fold an incoming batch into a dict keyed by element id. Mutates and returns
 * `existing` for convenience. Used on initial subscribe and on reconnection.
 */
export function mergeBatch(
  existing: Record<string, SceneElement>,
  incoming: Iterable<SceneElement>,
): Record<string, SceneElement> {
  for (const next of incoming) {
    const prev = existing[next.id];
    existing[next.id] = prev ? reconcile(prev, next) : next;
  }
  return existing;
}

/**
 * Produce the next (version, nonce) for a local mutation. The nonce is bounded
 * to stay inside Number.MAX_SAFE_INTEGER so JSON round-trips don't lose
 * precision — same bound as the C# implementation.
 */
export function nextStamp(currentVersion: number): { version: number; nonce: number } {
  // 2^52 is well inside MAX_SAFE_INTEGER (2^53 - 1) and matches long.MaxValue/2
  // closely enough for a tiebreaker nonce.
  const maxNonce = 2 ** 52;
  const nonce = Math.floor(Math.random() * maxNonce);
  return { version: currentVersion + 1, nonce };
}

// ---------------------------------------------------------------------------
// DynamoDB key conventions — must match SceneKeys.cs exactly.
// ---------------------------------------------------------------------------

export const SceneKeys = {
  mapPk: (mapId: string) => `MAP#${mapId}`,
  elementSk: (elementId: string) => `ELEMENT#${elementId}`,
  elementSkPrefix: 'ELEMENT#',
  mapMetaSk: 'META',

  libraryPk: (campaignId: string) => `LIBRARY#${campaignId}`,
  assetSk: (fileId: string) => `ASSET#${fileId}`,
  assetSkPrefix: 'ASSET#',

  filePk: (fileId: string) => `FILE#${fileId}`,
  fileMetaSk: 'META',
} as const;
